import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subscription } from 'rxjs';

import { TimerToastService } from './timer-toast.service';

export interface TimerToast {
    id: number;
    message: string;
    /** Optional. When omitted, the toast renders as a plain notification
     *  with no UNDO button — used for error/info messages where there's
     *  nothing for the user to undo. */
    undoFn?: () => void;
    removing?: boolean;
}

@Component({
    selector: 'timer-toast',
    templateUrl: './timer-toast.component.html',
    styleUrls: ['./timer-toast.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('toastAnim', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(16px)' }),
                animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(60px)' })),
            ]),
        ]),
    ],
})
export class TimerToastComponent implements OnInit, OnDestroy {
    toasts: TimerToast[] = [];
    private nextId = 0;
    private timers = new Map<number, any>();
    private serviceSub?: Subscription;

    constructor(
        private cdr: ChangeDetectorRef,
        private toastService: TimerToastService,
    ) {}

    ngOnInit(): void {
        // Bridge: any component that injects `TimerToastService` and calls
        // `.show(...)` ends up here. Lets sibling components (e.g. the
        // always-mounted timer-header) surface toasts without needing a
        // direct ViewChild reference to this component.
        this.serviceSub = this.toastService.messages$.subscribe(msg => {
            this.show(msg.text, msg.undoFn);
        });
    }

    ngOnDestroy(): void {
        this.serviceSub?.unsubscribe();
    }

    show(message: string, undoFn?: () => void, duration = 15000): void {
        const id = this.nextId++;
        this.toasts.push({ id, message, undoFn });
        this.cdr.markForCheck();

        const timer = setTimeout(() => this.dismiss(id), duration);
        this.timers.set(id, timer);
    }

    undo(toast: TimerToast): void {
        if (toast.undoFn) toast.undoFn();
        this.dismiss(toast.id);
    }

    dismiss(id: number): void {
        const timer = this.timers.get(id);
        if (timer) { clearTimeout(timer); this.timers.delete(id); }
        this.toasts = this.toasts.filter(t => t.id !== id);
        this.cdr.markForCheck();
    }

    trackById(_: number, toast: TimerToast): number {
        return toast.id;
    }
}
